/** 全局设置：鉴权 Key、主题、侧栏折叠。 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiKey, setApiKey } from '@/api/client';
import {
  applyTheme,
  readStoredMode,
  resolveMode,
  storeMode,
  type ResolvedMode,
  type ThemeMode,
} from '@/theme';

interface SettingsState {
  apiKey: string;
  /** 用户的选择，可能是 'system' */
  themeMode: ThemeMode;
  /** 'system' 解析后的实际主题。CSS 侧的 data-theme 与这个值一致。 */
  resolvedMode: ResolvedMode;
  /**
   * resolvedMode 的布尔镜像。
   * 图表层（Recharts）只能吃字面色值、拿不到 CSS 变量，需要一个「现在是不是深色」
   * 的判定来选色板，布尔比字符串比较更难写错。
   */
  darkMode: boolean;
  siderCollapsed: boolean;

  setApiKey: (key: string) => void;
  setThemeMode: (mode: ThemeMode) => void;
  /** 系统偏好变化时重新解析；仅 themeMode==='system' 时有意义 */
  syncSystemTheme: () => void;
  toggleSider: () => void;
}

// 首屏 data-theme 已由 index.html 的内联脚本落好，这里只是把同一份状态读进内存，
// 不再碰 DOM——重复写一次不会出错，但会让「谁负责首帧」变得含糊
const initialMode = readStoredMode();
const initialResolved = resolveMode(initialMode);

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // client.ts 有自己的 localStorage 读写（它不能依赖 React），这里取它的当前值做初始化
      apiKey: getApiKey(),
      themeMode: initialMode,
      resolvedMode: initialResolved,
      darkMode: initialResolved === 'dark',
      siderCollapsed: false,

      setApiKey: (key) => {
        // 单一真相仍在 client.ts，store 只是它的 UI 镜像
        setApiKey(key);
        set({ apiKey: key });
      },

      setThemeMode: (mode) => {
        storeMode(mode);
        const resolved = applyTheme(mode);
        set({ themeMode: mode, resolvedMode: resolved, darkMode: resolved === 'dark' });
      },

      syncSystemTheme: () => {
        if (get().themeMode !== 'system') return;
        const resolved = applyTheme('system');
        if (resolved === get().resolvedMode) return;
        set({ resolvedMode: resolved, darkMode: resolved === 'dark' });
      },

      toggleSider: () => set((s) => ({ siderCollapsed: !s.siderCollapsed })),
    }),
    {
      name: 'aigzl.settings',
      // apiKey 由 client.ts 持久化、themeMode 由 theme 模块持久化（键 aigzl.theme，
      // index.html 的内联脚本也读它）。这里只存自己独有的，免得同一份状态两处落盘后漂移。
      partialize: (s) => ({ siderCollapsed: s.siderCollapsed }),
    },
  ),
);
